import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Clock, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import type { MarginGroupBy, MarginReportResponse } from "@/types/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn, formatCompactNumber, formatDateTime } from "@/lib/utils";

/** yyyy-mm-dd for a Date, N days before today (UTC). */
function isoDay(daysAgo = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function formatEur(n: number): string {
  return `€${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPct(n: number | null): string {
  return n === null ? "—" : `${n.toFixed(1)}%`;
}

const GROUP_BY_OPTIONS: MarginGroupBy[] = ["instance", "employee_type", "action_type"];

export function MarginDashboardPage() {
  const { t } = useTranslation("billing");
  const [from, setFrom] = React.useState(() => isoDay(30));
  const [to, setTo] = React.useState(() => isoDay(0));
  const [instance, setInstance] = React.useState("");
  const [groupBy, setGroupBy] = React.useState<MarginGroupBy>("instance");

  const trimmedInstance = instance.trim();
  const { data, isLoading, error } = useQuery({
    queryKey: ["billing", "margin", from, to, trimmedInstance, groupBy],
    queryFn: () => {
      const params = new URLSearchParams({ from, to, groupBy });
      if (trimmedInstance) params.set("instance", trimmedInstance);
      return api.get<MarginReportResponse>(`/api/admin/billing/margin?${params.toString()}`);
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("margin.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("margin.subtitle")}</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <div className="space-y-1">
            <Label htmlFor="margin-from">{t("margin.from")}</Label>
            <Input
              id="margin-from"
              type="date"
              className="h-9 w-40"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="margin-to">{t("margin.to")}</Label>
            <Input
              id="margin-to"
              type="date"
              className="h-9 w-40"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="margin-instance">{t("margin.instance")}</Label>
            <Input
              id="margin-instance"
              className="h-9 w-48"
              placeholder={t("margin.instancePlaceholder")}
              value={instance}
              onChange={(e) => setInstance(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="margin-groupby">{t("margin.groupByLabel")}</Label>
            <Select value={groupBy} onValueChange={(v) => setGroupBy(v as MarginGroupBy)}>
              <SelectTrigger id="margin-groupby" className="h-9 w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUP_BY_OPTIONS.map((g) => (
                  <SelectItem key={g} value={g}>
                    {t(`margin.groupBy.${g}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : error ? (
        <p className="text-sm text-destructive">
          {t("loadFailed", { message: error instanceof Error ? error.message : String(error) })}
        </p>
      ) : !data ? null : (
        <>
          <AlertStrip data={data} />
          <MarginTable data={data} />
        </>
      )}
    </div>
  );
}

function AlertStrip({ data }: { data: MarginReportResponse }) {
  const { t } = useTranslation("billing");
  const { lowMargin, stale, unpricedModels } = data.alerts;
  if (lowMargin.length === 0 && stale.length === 0 && unpricedModels.length === 0) {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-muted-foreground">
          {t("margin.noAlerts")}
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <AlertCard
        Icon={AlertTriangle}
        tone="destructive"
        title={t("margin.lowMarginTitle", { threshold: data.lowMarginThresholdPct })}
        empty={t("margin.none")}
        items={lowMargin.map((a) => `${a.instanceName} — ${formatPct(a.marginPct)}`)}
      />
      <AlertCard
        Icon={Clock}
        tone="warning"
        title={t("margin.staleTitle")}
        empty={t("margin.staleEmpty")}
        items={stale.map((a) =>
          t("margin.staleItem", {
            instance: a.instanceName,
            date: a.lastReceivedAt ? formatDateTime(a.lastReceivedAt) : t("margin.never"),
          })
        )}
      />
      <AlertCard
        Icon={HelpCircle}
        tone="warning"
        title={t("margin.unpricedTitle")}
        empty={t("margin.unpricedEmpty")}
        items={unpricedModels.map((m) =>
          t("margin.unpricedItem", {
            model: `${m.provider || "?"}/${m.model || "?"}`,
            tokens: formatCompactNumber(m.tokens),
          })
        )}
      />
    </div>
  );
}

function AlertCard({
  Icon,
  tone,
  title,
  items,
  empty,
}: {
  Icon: typeof AlertTriangle;
  tone: "destructive" | "warning";
  title: string;
  items: string[];
  empty: string;
}) {
  const { t } = useTranslation("billing");
  return (
    <Card className={cn(items.length > 0 && tone === "destructive" && "border-destructive")}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Icon
            className={cn(
              "h-4 w-4",
              items.length === 0
                ? "text-muted-foreground"
                : tone === "destructive"
                  ? "text-destructive"
                  : "text-amber-600"
            )}
          />
          {title}
          <Badge variant={items.length === 0 ? "muted" : tone === "destructive" ? "destructive" : "secondary"}>
            {items.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground">{empty}</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {items.slice(0, 8).map((item, i) => (
              <li key={i} className="truncate" title={item}>
                {item}
              </li>
            ))}
            {items.length > 8 ? (
              <li className="text-muted-foreground">{t("margin.more", { count: items.length - 8 })}</li>
            ) : null}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function MarginTable({ data }: { data: MarginReportResponse }) {
  const { t } = useTranslation("billing");
  const keyHeader = t(`margin.groupBy.${data.groupBy}`);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("margin.tableTitle")}</CardTitle>
        <CardDescription>
          {t("margin.tableDescription", {
            from: formatDateTime(data.from),
            to: formatDateTime(data.to),
            value: formatEur(data.creditUnitValueEur),
            group: keyHeader.toLowerCase(),
            threshold: data.lowMarginThresholdPct,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{keyHeader}</TableHead>
                <TableHead className="text-right">{t("margin.actions")}</TableHead>
                <TableHead className="text-right">{t("margin.credits")}</TableHead>
                <TableHead className="text-right">{t("margin.revenue")}</TableHead>
                <TableHead className="text-right">{t("margin.realCost")}</TableHead>
                <TableHead className="text-right">{t("margin.margin")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                    {t("margin.empty")}
                  </TableCell>
                </TableRow>
              ) : (
                data.rows.map((row) => (
                  <TableRow
                    key={row.key}
                    className={cn(row.lowMargin && "bg-destructive/10")}
                  >
                    <TableCell className="font-mono text-xs font-medium">
                      <span className="inline-flex items-center gap-2">
                        {row.key}
                        {data.groupBy === "instance" && row.byoKey ? (
                          <Badge variant="muted">{t("margin.byoBadge")}</Badge>
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCompactNumber(row.actionCount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCompactNumber(row.creditsCharged)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatEur(row.revenueEur)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatEur(row.costEur)}</TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums",
                        row.lowMargin && "font-semibold text-destructive"
                      )}
                    >
                      {formatPct(row.marginPct)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
